import { DateTime } from 'luxon';

import { DASH, formatNumber } from '@/globals';

export interface ICuratedPackageDeliveryStatisticsEntryAPI {
  ecpm: number;
  min_cpm: number;
  max_cpm: number;
  site_count: number;
  unit_count: number;
  daily_impressions: number;
}

export interface ICuratedPackageDeliveryStatisticsAPI {
  by_package: Record<string, ICuratedPackageDeliveryStatisticsEntryAPI>;
  by_supplier: Record<string, ICuratedPackageDeliveryStatisticsEntryAPI>;
  total: ICuratedPackageDeliveryStatisticsEntryAPI;
  last_updated_utc: string;
}

export class CuratedPackageDeliveryStatisticsEntry {
  eCPM?: number;

  minCPM?: number;

  maxCPM?: number;

  siteCount?: number;

  unitCount?: number;

  dailyImpressions?: number;

  constructor(
    eCPM?: number,
    minCPM?: number,
    maxCPM?: number,
    siteCount?: number,
    unitCount?: number,
    dailyImpressions?: number,
  ) {
    this.eCPM = eCPM;
    this.minCPM = minCPM;
    this.maxCPM = maxCPM;
    this.siteCount = siteCount;
    this.unitCount = unitCount;
    this.dailyImpressions = dailyImpressions;
  }

  get dailyImpressionsFormatted(): string {
    return this.dailyImpressions ? formatNumber(this.dailyImpressions, '0.0a') : DASH;
  }

  get siteCountFormatted(): string {
    return this.siteCount ? formatNumber(this.siteCount, '0,0') : DASH;
  }

  get eCPMFormatted(): string {
    return this.eCPM ? formatNumber(this.eCPM, '0,0.00') : DASH;
  }

  get unitCountFormatted(): string {
    return this.unitCount ? formatNumber(this.unitCount, '0,0') : DASH;
  }

  static fromApi(apiObject: ICuratedPackageDeliveryStatisticsEntryAPI): CuratedPackageDeliveryStatisticsEntry {
    return new CuratedPackageDeliveryStatisticsEntry(
      apiObject.ecpm,
      apiObject.min_cpm,
      apiObject.max_cpm,
      apiObject.site_count,
      apiObject.unit_count,
      apiObject.daily_impressions,
    );
  }
}

export default class CuratedPackageDeliveryStatistics {
  byPackage?: Record<string, CuratedPackageDeliveryStatisticsEntry>;

  bySupplier?: Record<string, CuratedPackageDeliveryStatisticsEntry>;

  total?: CuratedPackageDeliveryStatisticsEntry;

  lastUpdatedUTC?: DateTime;

  constructor(
    byPackage?: Record<string, CuratedPackageDeliveryStatisticsEntry>,
    bySupplier?: Record<string, CuratedPackageDeliveryStatisticsEntry>,
    total?: CuratedPackageDeliveryStatisticsEntry,
    lastUpdatedUTC?: DateTime,
  ) {
    this.byPackage = byPackage;
    this.bySupplier = bySupplier;
    this.total = total;
    this.lastUpdatedUTC = lastUpdatedUTC;
  }

  static fromApi(apiObject: ICuratedPackageDeliveryStatisticsAPI): CuratedPackageDeliveryStatistics {
    const byPackage: Record<string, CuratedPackageDeliveryStatisticsEntry> = {};
    Object.keys(apiObject.by_package ?? {}).forEach((key) => {
      byPackage[key] = CuratedPackageDeliveryStatisticsEntry.fromApi(apiObject.by_package[key]);
    });
    const bySupplier: Record<string, CuratedPackageDeliveryStatisticsEntry> = {};
    Object.keys(apiObject.by_supplier ?? {}).forEach((key) => {
      bySupplier[key] = CuratedPackageDeliveryStatisticsEntry.fromApi(apiObject.by_supplier[key]);
    });
    return new CuratedPackageDeliveryStatistics(
      byPackage,
      bySupplier,
      CuratedPackageDeliveryStatisticsEntry.fromApi(apiObject.total),
      DateTime.fromISO(apiObject.last_updated_utc),
    );
  }
}
